import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  PaperPlaneTilt,
  Trash,
  PencilSimple,
  Briefcase,
  ClipboardText,
  MapPin,
  Calendar,
  X,
  Clock,
  Plus,
  ChatCircle,
  MagnifyingGlass,
  Lightning,
  Play,
  Stop,
  Users,
  Gear,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { CopilotQuickActions } from './CopilotQuickActions';
import { CopilotMessageList } from './CopilotMessageList';
import { copilotApi } from './copilotApi';
import { useCopilot } from './CopilotProvider';

// Quick Canvass command chips
const QC_COMMAND_CHIPS = [
  { id: 'start_draft', label: 'Start Draft Canvass', icon: Play, command: 'Start draft canvass' },
  { id: 'start_interest', label: 'Start Interest Canvass', icon: Play, command: 'Start interest canvass' },
  { id: 'close_canvass', label: 'Close Canvass', icon: Stop, command: 'Close canvass' },
  { id: 'forced_fill', label: 'Run Forced Fill', icon: Lightning, command: 'Run forced fill' },
  { id: 'set_crew', label: 'Set Crew Requirements', icon: Users, command: 'Set crew requirements' },
  { id: 'status', label: 'View Status', icon: Gear, command: 'Show canvass status' },
];

/**
 * Main Copilot Panel component - right-side drawer with conversation history
 */
export const CopilotPanel = ({ open, onOpenChange, context, setEntityContext }) => {
  const { token, user, api } = useAuth();
  const { 
    conversationId, 
    conversationHistory, 
    startNewConversation, 
    loadConversation, 
    updateConversation 
  } = useCopilot();
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingContext, setEditingContext] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [proposeLearningOpen, setProposeLearningOpen] = useState(false);
  const [proposalData, setProposalData] = useState({
    title: '',
    type: 'knowledge_note',
    reason: '',
    expected_impact: '',
  });
  
  // Command mode state
  const [commandMode, setCommandMode] = useState(true);
  const [pendingClarification, setPendingClarification] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Determine if we're in Quick Canvass module
  const isQuickCanvassModule = context?.module === 'quickcanvass' || 
    context?.path?.includes('/canvass') || 
    context?.path?.includes('/quickcanvass');

  // Load conversation history from provider
  useEffect(() => {
    if (conversationHistory.length > 0) {
      const formattedMessages = conversationHistory.map(msg => ({
        type: msg.role === 'user' ? 'user' : 'answer',
        content: msg.content,
      }));
      setMessages(formattedMessages);
    }
  }, [conversationHistory]);

  // Load conversations list when history panel opens
  useEffect(() => {
    if (showHistory && token && !searchQuery) {
      loadConversationsList();
    }
  }, [showHistory, token, searchQuery]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (searchQuery.length >= 2) {
      setSearching(true);
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const result = await copilotApi.searchConversations(searchQuery, token);
          setSearchResults(result.results || []);
        } catch (error) {
          console.error('Search failed:', error);
          setSearchResults([]);
        } finally {
          setSearching(false);
        }
      }, 300);
    } else {
      setSearchResults(null);
    }
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, token]);

  const loadConversationsList = async () => {
    setLoadingHistory(true);
    try {
      const result = await copilotApi.getConversations(token);
      setConversations(result.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleLoadConversation = async (convId) => {
    try {
      const conv = await copilotApi.getConversation(convId, token);
      loadConversation(conv.id, conv.messages || []);
      setShowHistory(false);
      toast.success('Conversation loaded');
    } catch (error) {
      console.error('Failed to load conversation:', error);
      toast.error('Failed to load conversation');
    }
  };

  const handleDeleteConversation = async (convId, e) => {
    e.stopPropagation();
    try {
      await copilotApi.deleteConversation(convId, token);
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (conversationId === convId) {
        startNewConversation();
        setMessages([]);
      }
      toast.success('Conversation deleted');
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      toast.error('Failed to delete conversation');
    }
  };

  const handleNewConversation = () => {
    startNewConversation();
    setMessages([]);
    setShowHistory(false);
    toast.success('New conversation started');
  };

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Focus input when panel opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const handleSendMessage = async (customMessage, answers = null) => {
    const messageText = customMessage || input.trim();
    if (!messageText || loading) return;

    // Add user message
    const userMessage = { type: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setPendingClarification(null);

    try {
      let response;
      
      // Use /agent/act for Quick Canvass command mode
      if (commandMode && isQuickCanvassModule) {
        const actResponse = await api.post('/agent/act', {
          message: messageText,
          context: {
            module: 'quickcanvass',
            job_id: context?.entity?.job_id || null,
            work_order_id: context?.entity?.work_order_id || null,
            canvass_window_id: context?.entity?.canvass_window_id || null,
          },
          answers: answers,
        });
        
        response = actResponse.data;
        
        // Handle clarification
        if (response.type === 'clarification') {
          setPendingClarification(response);
          setMessages(prev => [...prev, {
            type: 'clarification',
            content: response.message,
            questions: response.questions || [],
          }]);
          setLoading(false);
          return;
        }
        
        // Handle executed action
        if (response.type === 'executed') {
          const agentMessage = {
            type: 'executed',
            content: response.message,
            executed_action: response.executed_action,
          };
          setMessages(prev => [...prev, agentMessage]);
          
          // Show success toast
          toast.success(response.executed_action?.intent?.replace(/_/g, ' ') || 'Action completed');
          setLoading(false);
          return;
        }
        
        // Handle error
        if (response.type === 'error') {
          setMessages(prev => [...prev, {
            type: 'error',
            content: response.message,
            suggestions: response.suggestions,
          }]);
          setLoading(false);
          return;
        }
      } else {
        // Regular copilot message
        response = await copilotApi.sendMessage(messageText, context, token, conversationId);
      }
      
      // Update conversation state in provider
      if (response.conversation_id) {
        updateConversation(response.conversation_id, messageText, response.message);
      }
      
      // Add agent response
      const agentMessage = {
        type: response.type || 'answer',
        content: response.message,
        questions: response.questions || [],
        suggested_actions: response.suggested_actions || [],
        data: response.data || {},
      };
      setMessages(prev => [...prev, agentMessage]);
    } catch (error) {
      console.error('Copilot error:', error);
      setMessages(prev => [
        ...prev,
        {
          type: 'error',
          content: error.response?.data?.message || 'Sorry, I encountered an error. Please try again.',
        },
      ]);
      toast.error('Failed to get response from Copilot');
    } finally {
      setLoading(false);
    }
  };

  // Handle clarification answer
  const handleClarificationAnswer = (questionId, value) => {
    const answers = { [questionId]: value };
    
    // Re-send the original command with the answer
    if (pendingClarification) {
      const originalMessage = messages.filter(m => m.type === 'user').pop()?.content || '';
      handleSendMessage(originalMessage, answers);
    }
  };

  const handleQuickAction = (prompt, actionId) => {
    if (actionId === 'propose') {
      setProposeLearningOpen(true);
    } else {
      handleSendMessage(prompt);
    }
  };

  const handleSuggestedAction = async (action) => {
    try {
      if (action.type === 'SAVE_PROPOSED_LEARNING') {
        setProposalData(prev => ({
          ...prev,
          ...action.payload,
        }));
        setProposeLearningOpen(true);
      } else if (action.type === 'ADD_BACKLOG') {
        const result = await copilotApi.addToBacklog(
          {
            module: context.module || 'platform',
            problem: 'User-identified improvement opportunity',
            suggested_change: 'Review and implement suggested improvement',
            ...action.payload,
          },
          token
        );
        toast.success('Added to improvement backlog');
        setMessages(prev => [
          ...prev,
          {
            type: 'answer',
            content: `Added to improvement backlog (ID: ${result.backlog_id?.slice(0, 8)}...)`,
          },
        ]);
      } else if (action.type === 'GENERATE_REPORT') {
        handleSendMessage('Generate a detailed report for the current context.');
      }
    } catch (error) {
      console.error('Action error:', error);
      toast.error('Failed to perform action');
    }
  };

  const handleProposeLearning = async () => {
    try {
      const result = await copilotApi.proposeLearning(
        {
          title: proposalData.title,
          type: proposalData.type,
          module: context.module || 'platform',
          reason: proposalData.reason,
          expected_impact: proposalData.expected_impact,
          priority: 'medium',
          risk_level: 'low',
          scope: 'global',
          source_type: 'user_answer',
        },
        token
      );
      
      toast.success('Proposed learning created');
      setProposeLearningOpen(false);
      setProposalData({ title: '', type: 'knowledge_note', reason: '', expected_impact: '' });
      
      setMessages(prev => [
        ...prev,
        {
          type: 'answer',
          content: `Proposed learning created successfully (ID: ${result.proposal_id?.slice(0, 8)}...). An admin will review it.`,
        },
      ]);
    } catch (error) {
      console.error('Propose learning error:', error);
      toast.error('Failed to create proposed learning');
    }
  };

  const clearChat = () => {
    handleNewConversation();
  };

  const contextChips = [
    { label: 'Module', value: context.module || 'platform', icon: Briefcase },
    context.entity?.job_id && { label: 'Job', value: context.entity.job_id.slice(0, 8), icon: ClipboardText },
    context.entity?.work_order_id && { label: 'WO', value: context.entity.work_order_id.slice(0, 8), icon: ClipboardText },
    context.entity?.station_id && { label: 'Station', value: context.entity.station_id.slice(0, 8), icon: MapPin },
    context.filters?.date_from && { label: 'From', value: context.filters.date_from, icon: Calendar },
    context.filters?.date_to && { label: 'To', value: context.filters.date_to, icon: Calendar },
  ].filter(Boolean);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      {/* Overlay - only covers when panel is open, pointer-events so sidebar still works */}
      {open && (
        <div
          className="fixed inset-0 bg-black/30 z-[9998]"
          onClick={() => onOpenChange(false)}
          style={{ pointerEvents: 'auto' }}
        />
      )}

      {/* Copilot Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-[400px] sm:w-[450px] bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-[9999] flex flex-col ${
          open ? 'translate-x-0' : 'translate-x-full pointer-events-none'
        }`}
        data-testid="copilot-panel"
      >
        {/* Header */}
        <div className="p-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="font-display text-lg font-semibold">Copilot</h2>
              {isQuickCanvassModule && (
                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                  <Lightning size={10} className="mr-1" />
                  Command Mode
                </Badge>
              )}
            </div>
            <div className="flex gap-1">
              {isQuickCanvassModule && (
                <div className="flex items-center gap-2 mr-2 pr-2 border-r">
                  <Label htmlFor="command-mode" className="text-xs text-slate-500">Commands</Label>
                  <Switch 
                    id="command-mode" 
                    checked={commandMode} 
                    onCheckedChange={setCommandMode}
                    className="scale-75"
                  />
                </div>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowHistory(!showHistory)}
                className="h-8 w-8"
                data-testid="copilot-history-btn"
                title="Conversation History"
              >
                <Clock size={16} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleNewConversation}
                className="h-8 w-8"
                data-testid="copilot-new-btn"
                title="New Conversation"
              >
                <Plus size={16} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={clearChat}
                className="h-8 w-8"
                data-testid="copilot-clear-btn"
                title="Clear Chat"
              >
                <Trash size={16} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onOpenChange(false)}
                className="h-8 w-8"
                data-testid="copilot-close-btn"
              >
                <X size={16} />
              </Button>
            </div>
          </div>

          {/* Context chips */}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {contextChips.map((chip, i) => {
              const Icon = chip.icon;
              return (
                <Badge
                  key={i}
                  variant="outline"
                  className="text-xs gap-1 cursor-pointer hover:bg-slate-100"
                  onClick={() => setEditingContext(true)}
                >
                  <Icon size={12} />
                  {chip.label}: {chip.value}
                </Badge>
              );
            })}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditingContext(true)}
              className="h-6 px-2 text-xs"
            >
              <PencilSimple size={12} />
            </Button>
          </div>

          {/* Quick Canvass Command Chips */}
          {isQuickCanvassModule && commandMode && (
            <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-slate-100">
              {QC_COMMAND_CHIPS.map((chip) => {
                const Icon = chip.icon;
                return (
                  <Button
                    key={chip.id}
                    variant="outline"
                    size="sm"
                    onClick={() => handleSendMessage(chip.command)}
                    disabled={loading}
                    className="h-7 text-xs gap-1 bg-slate-50 hover:bg-blue-50 hover:border-blue-300"
                    data-testid={`qc-cmd-${chip.id}`}
                  >
                    <Icon size={12} />
                    {chip.label}
                  </Button>
                );
              })}
            </div>
          )}
        </div>

        {/* Conversation History Sidebar */}
        {showHistory && (
          <div className="absolute top-0 left-0 w-full h-full bg-white z-10 flex flex-col">
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">Conversation History</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setShowHistory(false);
                    setSearchQuery('');
                    setSearchResults(null);
                  }}
                  className="h-8 w-8"
                >
                  <X size={16} />
                </Button>
              </div>
              {/* Search Input */}
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search conversations..."
                  className="pl-9 pr-8"
                  data-testid="conversation-search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSearchResults(null);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {/* Search Results or Conversation List */}
              {searching ? (
                <div className="text-center py-8 text-slate-500">Searching...</div>
              ) : searchResults !== null ? (
                // Show search results
                searchResults.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <MagnifyingGlass size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No results for "{searchQuery}"</p>
                    <p className="text-xs mt-1">Try different keywords</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500 px-2 mb-2">
                      {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
                    </p>
                    {searchResults.map((conv) => (
                      <div
                        key={conv.id}
                        onClick={() => {
                          handleLoadConversation(conv.id);
                          setSearchQuery('');
                          setSearchResults(null);
                        }}
                        className={`p-3 rounded-lg cursor-pointer hover:bg-slate-100 border transition-colors ${
                          conversationId === conv.id ? 'bg-blue-50 border-blue-200' : 'border-slate-200'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-700">
                              {conv.preview}
                            </p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                              <Badge variant="outline" className="text-[10px] px-1.5">
                                {conv.module || 'general'}
                              </Badge>
                              <span>{formatDate(conv.updated_at)}</span>
                              <span>• {conv.message_count} msgs</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : loadingHistory ? (
                <div className="text-center py-8 text-slate-500">Loading...</div>
              ) : conversations.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <ChatCircle size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No conversations yet</p>
                  <p className="text-xs mt-1">Start chatting to create history</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {conversations.map((conv) => (
                    <div
                      key={conv.id}
                      onClick={() => handleLoadConversation(conv.id)}
                      className={`p-3 rounded-lg cursor-pointer hover:bg-slate-100 border transition-colors ${
                        conversationId === conv.id ? 'bg-blue-50 border-blue-200' : 'border-slate-200'
                      }`}
                      data-testid={`conv-item-${conv.id.slice(0, 8)}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {conv.preview || 'New conversation'}
                          </p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                            <Badge variant="outline" className="text-[10px] px-1.5">
                              {conv.module || 'general'}
                            </Badge>
                            <span>{formatDate(conv.updated_at)}</span>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => handleDeleteConversation(conv.id, e)}
                          className="h-6 w-6 text-slate-400 hover:text-red-500"
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-3 border-t">
              <Button
                onClick={handleNewConversation}
                className="w-full"
                variant="outline"
              >
                <Plus size={16} className="mr-2" />
                New Conversation
              </Button>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="flex-shrink-0">
          <CopilotQuickActions onAction={handleQuickAction} disabled={loading} />
        </div>

        {/* Messages - scrollable area */}
        <div className="flex-1 overflow-y-auto p-4">
          <CopilotMessageList
            messages={messages}
            onSuggestedAction={handleSuggestedAction}
            loading={loading}
          />
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-slate-200 flex-shrink-0">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
            className="flex gap-2"
          >
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Copilot anything..."
              disabled={loading}
              className="flex-1"
              data-testid="copilot-input"
            />
            <Button
              type="submit"
              disabled={!input.trim() || loading}
              className="bg-primary"
              data-testid="copilot-send-btn"
            >
              <PaperPlaneTilt size={18} weight="fill" />
            </Button>
          </form>
        </div>
      </div>

      {/* Propose Learning Dialog */}
      <Dialog open={proposeLearningOpen} onOpenChange={setProposeLearningOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Propose a Learning</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={proposalData.title}
                onChange={(e) => setProposalData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Brief title for this learning..."
                data-testid="propose-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={proposalData.type}
                onValueChange={(v) => setProposalData(prev => ({ ...prev, type: v }))}
              >
                <SelectTrigger data-testid="propose-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="config_change">Config Change</SelectItem>
                  <SelectItem value="knowledge_note">Knowledge Note</SelectItem>
                  <SelectItem value="workflow_change">Workflow Change</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea
                value={proposalData.reason}
                onChange={(e) => setProposalData(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Why is this learning needed?"
                data-testid="propose-reason-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Expected Impact</Label>
              <Textarea
                value={proposalData.expected_impact}
                onChange={(e) => setProposalData(prev => ({ ...prev, expected_impact: e.target.value }))}
                placeholder="What impact will this have?"
                data-testid="propose-impact-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleProposeLearning}
              disabled={!proposalData.title || !proposalData.reason}
              className="bg-primary"
              data-testid="propose-submit-btn"
            >
              Create Proposal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Context Dialog */}
      <Dialog open={editingContext} onOpenChange={setEditingContext}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Context</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Context is automatically detected from your current page.
              You can manually set entity IDs if needed.
            </p>
            <div className="space-y-2">
              <Label>Module: {context.module || 'platform'}</Label>
            </div>
            <div className="space-y-2">
              <Label>Job ID (optional)</Label>
              <Input
                value={context.entity?.job_id || ''}
                onChange={(e) => setEntityContext({ job_id: e.target.value || undefined })}
                placeholder="Enter job UUID..."
              />
            </div>
            <div className="space-y-2">
              <Label>Work Order ID (optional)</Label>
              <Input
                value={context.entity?.work_order_id || ''}
                onChange={(e) => setEntityContext({ work_order_id: e.target.value || undefined })}
                placeholder="Enter work order UUID..."
              />
            </div>
            <div className="space-y-2">
              <Label>Station ID (optional)</Label>
              <Input
                value={context.entity?.station_id || ''}
                onChange={(e) => setEntityContext({ station_id: e.target.value || undefined })}
                placeholder="Enter station UUID..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setEditingContext(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CopilotPanel;
